import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, Check, ArrowRight, ImageOff, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";
import HeroBackdrop from "@/components/HeroBackdrop";
import { getService, SERVICES } from "@/data/services";
import { SITE } from "@/data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <>
      <Seo
        title={service.name}
        description={`${service.name} from Kensmarc Electric — ${service.short} Serving 91702 and Los Angeles County. Request a free quote.`}
        path={`/services/${service.slug}`}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#1a1a22] bg-[#0b0b0f]">
        <HeroBackdrop />
        <div className="container-kx relative py-16 sm:py-20 lg:py-24">
          <nav className="mb-6 flex items-center gap-1.5 text-sm text-neutral-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/services" className="hover:text-amber-400">Services</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-neutral-300">{service.name}</span>
          </nav>
          <span className="kx-badge"><service.Icon className="h-3.5 w-3.5" /> {service.category}</span>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
            {service.name}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-300">{service.description}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/request-a-quote" data-testid="detail-quote-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-[#0A0A0C] hover:bg-amber-300">
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={SITE.phonePrimary.tel} data-testid="detail-call-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2e2e39] px-7 py-3.5 text-sm font-bold text-white hover:border-amber-400/60">
              <Phone className="h-4 w-4 text-amber-400" /> Call {SITE.phonePrimary.display}
            </a>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-kx grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <h2 className="font-display text-2xl font-bold uppercase text-white">Common types of work</h2>
            <p className="mt-3 text-neutral-400">
              A few examples of what {service.name.toLowerCase()} can include. Every project is different —
              tell us about yours and we'll put together a quote.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {service.workTypes.map((w) => (
                <li key={w} className="flex items-start gap-3 rounded-lg border border-[#20202a] bg-[#101017] p-4">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
                  <span className="text-sm text-neutral-200">{w}</span>
                </li>
              ))}
            </ul>

            {/* Project media slot */}
            <div className="mt-10">
              <h2 className="font-display text-2xl font-bold uppercase text-white">Project media</h2>
              <div className="mt-4 flex items-center gap-4 rounded-xl border border-dashed border-[#2a2a34] bg-[#101017] p-6 text-neutral-400">
                <ImageOff className="h-7 w-7 shrink-0 text-amber-400" />
                <p className="text-sm">
                  Real {service.name.toLowerCase()} photos and videos from Kensmarc Electric will be featured
                  here. See our{" "}
                  <Link to="/our-work" className="font-semibold text-amber-400 hover:underline">recent projects</Link>.
                </p>
              </div>
            </div>

            <div className="mt-10 rounded-xl border border-[#20202a] bg-[#0c0c11] p-6">
              <h3 className="font-display text-lg font-bold uppercase text-white">Service area</h3>
              <p className="mt-2 text-sm text-neutral-400">
                Kensmarc Electric provides {service.name.toLowerCase()} near {SITE.serviceZip} and throughout{" "}
                {SITE.county}, depending on location and project requirements.{" "}
                <Link to="/service-area" className="font-semibold text-amber-400 hover:underline">Check your area →</Link>
              </p>
            </div>
          </div>

          {/* Quote form */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">Get a quote for {service.name.toLowerCase()}</h2>
            <QuoteForm defaultService={service.name} />
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="border-t border-[#1a1a22] bg-[#0c0c11] py-16">
        <div className="container-kx">
          <h2 className="font-display text-2xl font-bold uppercase text-white">Explore other services</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="kx-card kx-card-hover group flex items-center gap-3 p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-400/10 text-amber-400">
                  <s.Icon className="h-5 w-5" />
                </div>
                <span className="font-semibold text-white group-hover:text-amber-400">{s.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
